import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router';

import {
  filterNavItemsForUser,
  type NavItemConfig,
  type RouteNavConfig,
} from '@revelio/auth';
import { FeatureFlag } from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';

import { useHideNav } from '../../../shared/hooks/useHideNav';
import { useUnleashUserFlagsReady } from '../../../shared/unleash-flags/unleash-flags';
import { usePageManager } from '../../CustomDash/components/PageManagerProvider/PageManagerProvider';
import { useGlobalAIAssistant } from '../../global-ai-assistant/global-ai-assistant-context';
import { ExpandedNav } from './expanded-nav/ExpandedNav';
import { Logo } from './Logo';
import { AccountMenu } from './menus/account-menu';
import { TutorialsAnchor } from './menus/tutorials-anchor';
import styles from './side-nav.module.css';
import { useEnforceUserIntroduction } from './useEnforceUserIntroduction';

interface SideNavProps {
  items: (NavItemConfig | RouteNavConfig)[];
  version?: string;
  onOpenImpersonationModal?: () => void;
}

function SideNav(props: SideNavProps) {
  const { loggedInUser } = useEnforceUserIntroduction();
  const { pages } = usePageManager();

  const navPages = useMemo<RouteNavConfig[]>(() => {
    if (isEmpty(loggedInUser)) {
      return [];
    }

    const userTabs = loggedInUser.tabs || [];
    const userRole = loggedInUser.role;

    const visible = filterNavItemsForUser(
      props.items,
      userTabs,
      userRole
    ).filter((page) => !page.excludeFromSideNavButtons);

    return [
      ...visible.filter((page) => page.isSourcesTab),
      ...visible.filter((page) => !page.isSourcesTab),
    ];
  }, [loggedInUser, props.items]);

  const hideNav = useHideNav();
  if (hideNav) {
    return (
      <div className={styles.content}>
        <Outlet />
      </div>
    );
  }

  return (
    <div className={styles.railGrid}>
      <div className={classNames('rl-sidenav', styles.rail)}>
        <div className={classNames(styles.sidebar, styles.railContent)}>
          <HomeLogo />

          <ExpandedNav
            className={styles.expandedNav}
            items={navPages}
            pages={pages}
            footer={
              <TutorialsAnchor>
                {(tutorials) => (
                  <AccountMenu
                    version={`v${props.version}`}
                    onOpenImpersonationModal={props.onOpenImpersonationModal}
                    tutorials={tutorials}
                  />
                )}
              </TutorialsAnchor>
            }
          />
        </div>
      </div>
      <div>
        <Outlet></Outlet>
      </div>
    </div>
  );
}

export default SideNav;

const LogoButton = ({
  onClick,
}: {
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}) => {
  return (
    <div
      data-testid="sidebar-logo"
      className={classNames(styles.logoBox, styles.sidebarLogo)}
      onClick={onClick}
    >
      <Logo />
    </div>
  );
};

const AiWrappedHomeLogo = ({
  onClick,
}: {
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}) => {
  const { persistence } = useGlobalAIAssistant();

  const handleGoHome: React.MouseEventHandler<HTMLDivElement> = (e) => {
    persistence.newChat();
    onClick?.(e);
  };

  return <LogoButton onClick={handleGoHome} />;
};

const HomeLogo = () => {
  const assistantDisabled = useUnleashFlag(FeatureFlag.DisableAIAssistant);
  const flagsReadyForUser = useUnleashUserFlagsReady();
  const canShowAi = flagsReadyForUser && !assistantDisabled;
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  if (!canShowAi) return <LogoButton onClick={handleGoHome} />;
  return <AiWrappedHomeLogo onClick={handleGoHome} />;
};
